import type { IFileSystem } from '@erp/native/interfaces';

export function createWebFileSystem(): IFileSystem {
  return {
    async saveFile(req) {
      // Browsers expose two paths: the legacy `<a download>` trick (works
      // everywhere) and the new File System Access API (Chromium only).
      // We use the modern API when available so the user picks a folder
      // through the OS dialog instead of just dumping into Downloads.
      const w = window as unknown as {
        showSaveFilePicker?: (opts: {
          suggestedName?: string;
          types?: Array<{ description?: string; accept: Record<string, string[]> }>;
        }) => Promise<{
          createWritable: () => Promise<{
            write: (data: BlobPart) => Promise<void>;
            close: () => Promise<void>;
          }>;
          name: string;
        }>;
      };
      if (w.showSaveFilePicker) {
        try {
          const handle = await w.showSaveFilePicker({
            suggestedName: req.suggestedName,
            types: req.filters?.map((f) => ({
              description: f.name,
              accept: { [req.mimeType]: f.extensions.map((e) => `.${e}`) },
            })),
          });
          const writable = await handle.createWritable();
          await writable.write(new Blob([new Uint8Array(req.data)], { type: req.mimeType }));
          await writable.close();
          return { saved: true, path: handle.name };
        } catch (err) {
          // AbortError = user cancelled.
          const isAbort =
            err instanceof DOMException && err.name === 'AbortError';
          if (isAbort) return { saved: false, path: '' };
          // Fall through to <a download> on any other error.
        }
      }
      const blob = new Blob([new Uint8Array(req.data)], { type: req.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = req.suggestedName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so the download has a chance to start in slow browsers.
      setTimeout(() => URL.revokeObjectURL(url), 4_000);
      return { saved: true, path: req.suggestedName };
    },
    async openFile(req) {
      const input = document.createElement('input');
      input.type = 'file';
      if (req.multiple) input.multiple = true;
      if (req.filters) {
        input.accept = req.filters
          .flatMap((f) => f.extensions.map((e) => `.${e}`))
          .join(',');
      }
      const files = await new Promise<File[]>((resolve) => {
        input.onchange = () => {
          resolve(Array.from(input.files ?? []));
        };
        input.click();
      });
      const out = [];
      for (const f of files) {
        const buf = new Uint8Array(await f.arrayBuffer());
        out.push({
          name: f.name,
          path: '',
          data: buf,
          size: f.size,
          mimeType: f.type,
        });
      }
      return out;
    },
    async openExternal(pathOrUrl) {
      window.open(pathOrUrl, '_blank', 'noopener,noreferrer');
    },
    async revealInFolder() {
      // No-op on web — there is no concept of "reveal in folder".
    },
  };
}
